import { Link, useParams } from 'react-router-dom';
import { getArticleBySlug, blogArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL;
const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png';

function buildBlogPostingJsonLd(article: ReturnType<typeof getArticleBySlug>) {
  if (!article) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt || article.sections?.[0]?.content?.slice(0, 160) || article.title,
    image: article.featuredImageUrl || article.imageUrl,
    datePublished: '2026-07-08',
    dateModified: '2026-07-08',
    author: {
      '@type': 'Organization',
      name: 'Kakobuy Finds',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Kakobuy Finds',
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/blog/${article.id}`,
    },
    inLanguage: 'pl',
    keywords: article.secondaryKeywords?.join(', ') || article.primaryKeyword,
    about: {
      '@type': 'Thing',
      name: article.primaryKeyword,
    },
  };
}

function buildFaqPageJsonLd(article: ReturnType<typeof getArticleBySlug>) {
  if (!article || !article.faq || article.faq.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;

  if (!article) {
    return <NotFound />;
  }

  const blogJsonLd = buildBlogPostingJsonLd(article);
  const faqJsonLd = buildFaqPageJsonLd(article);

  return (
    <>
      {blogJsonLd && <JsonLd data={blogJsonLd} />}
      {faqJsonLd && <JsonLd data={faqJsonLd} />}
      <main className="min-h-screen bg-background-50">
        {/* Navbar */}
        <nav className="sticky top-0 z-50 bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
            <Link
              to="/"
              className="font-heading text-lg font-semibold text-foreground-950 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              ← kakobuyfinds<span className="text-primary-500">.forum</span>
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              kakobuy sheet
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </nav>

        <article className="max-w-[800px] mx-auto px-4 md:px-6 py-10 md:py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8">
            <Link to="/" className="hover:text-primary-500 transition-colors">Strona główna</Link>
            <span>/</span>
            <span className="text-foreground-600">Blog</span>
            <span>/</span>
            <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">Poradnik</span>
              <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">{article.primaryKeyword}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-foreground-500 text-sm md:text-base">
              Ostatnia aktualizacja: lipiec 2026 &bull; Czas czytania: {article.readTime}
            </p>
          </header>

          {/* Featured Image */}
          {article.featuredImageUrl && (
            <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.featuredImageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Table of Contents */}
          <div className="bg-background-100 rounded-2xl p-6 mb-10">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Spis treści</h2>
            <ol className="space-y-2">
              {article.sections.map((section, i) => (
                <li key={i} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                  <a href={`#section-${i + 1}`} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-600 text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <span className="truncate">{section.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* Article Content */}
          <div className="space-y-10">
            {article.sections.map((section, i) => (
              <section key={i} id={`section-${i + 1}`}>
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">
                  {section.title}
                </h3>
                <p className="text-foreground-700 leading-relaxed text-[15px] md:text-base">
                  {section.content}
                </p>
              </section>
            ))}
          </div>

          {/* CTA Box */}
          <div className="mt-12 bg-primary-500 rounded-2xl p-8 text-center">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-background-50 mb-3">
              Gotowy na zakupy?
            </h3>
            <p className="text-background-50/80 text-sm mb-6 max-w-md mx-auto">
              Sprawdź pełną wersję arkusza Kakobuy Spreadsheet na kaspreadsheet.com i zacznij oszczędzać już dziś.
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 text-sm font-semibold rounded-full hover:bg-background-50/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Przejdź do kakobuy sheet
              <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
            </a>
          </div>

          {/* FAQ Section */}
          <section className="mt-14">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-8">
              Często zadawane pytania
            </h2>
            <div className="space-y-4">
              {article.faq.map((item, i) => (
                <details key={i} className="bg-background-100 rounded-2xl overflow-hidden group">
                  <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
                    <span className="text-sm font-semibold text-foreground-900 pr-4">{item.question}</span>
                    <span className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 group-open:rotate-45 transition-transform duration-200">
                      <i className="ri-add-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                    </span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Related Articles */}
          <section className="mt-14 border-t border-background-200 pt-10">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-6">
              Powiązane artykuły
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {article.relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/blog/${rel.id}`}
                  className="bg-background-100 rounded-xl p-5 hover:bg-primary-50 transition-colors group cursor-pointer"
                >
                  <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug">
                    {rel.title}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-xs text-primary-500 mt-3">
                    Czytaj więcej
                    <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center"></i>
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Categories quick links */}
          <section className="mt-14 border-t border-background-200 pt-10">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-6">
              Przeglądaj kategorie
            </h2>
            <div className="flex flex-wrap gap-2">
              {categories.slice(0, 8).map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-2 bg-background-100 text-foreground-700 text-xs font-medium rounded-full hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </section>

          {/* Back to Home */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Powrót do strony głównej
            </Link>
          </div>
        </article>
      </main>

      {/* Minimal Footer */}
      <footer className="w-full bg-primary-900 text-background-50/80 text-xs text-center py-6">
        <p>&copy; 2025 KakobuyFinds.forum &mdash; <Link to="/" className="hover:text-background-50 transition-colors">Strona główna</Link></p>
      </footer>
    </>
  );
}